'use client';

import { cn } from '../../../../lib/utils';
import { CloudUpload, CloudUploadFill } from '@crabs-ai/web-icons';
import { cva } from 'class-variance-authority';
import { useState } from 'react';

const fileDropZoneVariants = cva(
  [
    'flex items-center justify-center gap-2xs rounded-xs border-[1px] py-lg px-md text-sys-cont-neutral-default',
  ],
  {
    variants: {
      state: {
        default: '',
        hovering: '',
        pressing: '',
        drop: '',
      },
    },
    compoundVariants: [
      {
        state: 'default',
        className: [
          'border-sys-style-neutral-deep bg-sys-bg-surface-2 border-dashed ',
          '[&_path]:fill-sys-cont-neutral-default',
        ],
      },
      {
        state: 'hovering',
        className: [
          'border-sys-style-neutral-deep bg-sys-style-neutral-light border-dashed ',
          '[&_path]:fill-sys-cont-neutral-default',
        ],
      },
      {
        state: 'pressing',
        className: [
          'border-sys-style-neutral-deep bg-sys-style-neutral-default border-dashed ',
          '[&_path]:fill-sys-cont-neutral-default',
        ],
      },
      {
        state: 'drop',
        className: [
          'border-[2px] border-sys-style-primary-default bg-sys-bg-surface-primary',
          '[&_path]:fill-sys-cont-neutral-default',
        ],
      },
    ],
    defaultVariants: {
      state: 'default',
    },
  },
);

export interface FileDropZoneProps {
  /** 파일 드롭존의 상태 */
  state?: 'default' | 'hovering' | 'pressing' | 'drop';
  /** 드롭존에 표시할 텍스트 */
  labelText?: string;
  /** 파일 업로드 핸들러 */
  onDrop?: (e: React.DragEvent<HTMLDivElement>) => void;
  /** 클릭 핸들러 */
  onClick?: () => void;
}

export default function FileDropZone({
  ...props
}: FileDropZoneProps & React.ComponentProps<'div'>) {
  const {
    state = 'default',
    labelText = '파일을 드래그하여 업로드하세요',
    onDrop,
    onClick,
    className,
    ...rest
  } = props;

  const [internalState, setInternalState] = useState<
    'default' | 'hovering' | 'pressing' | 'drop'
  >(state);

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setInternalState('drop');
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setInternalState('drop');
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setInternalState('default');
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setInternalState('drop');

    if (onDrop) {
      onDrop(e);
    }

    // 잠시 후 상태를 default로 변경
    setTimeout(() => {
      setInternalState('default');
    }, 500);
  };

  const handleMouseDown = () => {
    setInternalState('pressing');
    if (onClick) {
      onClick();
    }
  };

  const handleMouseUp = () => {
    setInternalState('hovering');
  };

  const handleMouseEnter = () => {
    setInternalState('hovering');
  };

  const handleMouseLeave = () => {
    setInternalState('default');
  };

  return (
    <div
      data-custom-state={internalState}
      className={cn(
        'group',
        fileDropZoneVariants({ state: internalState }),
        className,
      )}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      {...rest}
    >
      <div className="flex flex-col items-center gap-2xs">
        {internalState === 'default' ? (
          <CloudUpload className="size-[32px]" />
        ) : (
          <CloudUploadFill className="size-[32px]" />
        )}
        <div className="text-center text-body-small whitespace-pre-line">
          {labelText.replace(/\\n/g, '\n')}
        </div>
      </div>
    </div>
  );
}
